import React, { useEffect, useRef, useState } from "react";
import { css } from "@emotion/react";
import { Common } from "@src/styles/Common";
import { MixedChart } from "@src/components";
import DateRangeCalendar, {
    DateRangeCalendarType,
} from "@src/components/calendar/DateRangeCalendar";
import {
    initTime,
    OnbidGuestStatType,
    useHomeOnbidGuestChart,
} from "@src/hooks/queries/useHomeChart";
import { dateFormat } from "@src/utils/dateUtil";
import moment from "moment";
import "moment/locale/ko";
moment.locale("ko");

const GUEST_COLOR = "#7C4DFF";
const TOKEN_COLOR = "#00B8D9";

function OnbidGuestStatsChart() {
    const [filter, setFilter] = useState<OnbidGuestStatType>({
        startDate: dateFormat(initTime.day.startDate),
        endDate: dateFormat(initTime.day.endDate),
    });

    const parseOnbidGuestChart = (data: any) => {
        return {
            labels: data.map((v: any) => v.date),
            datasets: [
                {
                    type: "bar",
                    label: "접속자 수",
                    data: data.map((v: any) => v.guestCount),
                    borderColor: GUEST_COLOR,
                    backgroundColor: GUEST_COLOR,
                    pointRadius: 2,
                    pointHoverBackgroundColor: Common.colors.white,
                    pointHoverBorderWidth: 3,
                    pointHoverRadius: 5,
                },
                {
                    type: "line",
                    label: "매물 조회 수",
                    data: data.map((v: any) => v.tokenCount),
                    borderColor: TOKEN_COLOR,
                    backgroundColor: TOKEN_COLOR,
                    borderWidth: 3,
                    pointRadius: 2,
                    pointHoverBackgroundColor: Common.colors.white,
                    pointHoverBorderWidth: 3,
                    pointHoverRadius: 5,
                },
            ],
        };
    };

    const { isLoading, data, isError } = useHomeOnbidGuestChart(
        filter,
        parseOnbidGuestChart,
    );

    const [chartData, setChartData] = useState<any>([]);

    useEffect(() => {
        if (!isLoading && !isError) {
            setChartData(data);
        }
    }, [data, isLoading, isError]);

    const sumOf = (index: number) =>
        (chartData?.datasets?.[index]?.data ?? []).reduce(
            (acc: number, v: any) => acc + Number(v ?? 0),
            0,
        );
    const summary = { guest: sumOf(0), token: sumOf(1) };

    const options = {
        scales: {
            x: {
                type: "time",
                time: {
                    unit: "day",
                    displayFormats: {
                        day: "MM.dd",
                    },
                    tooltipFormat: "YYY.MM.dd",
                },
            },
            //* 접속자 수와 조회 수를 한 축에 둔다.
            //* 좌우 축을 나누면 MixedChart 가 축을 별도 캔버스로 복제하는데,
            //* 라벨이 9개 이하면 본 캔버스가 스크롤 없이 다 보여서 눈금이 겹쳐 그려진다.
            y: {
                beginAtZero: true,
                grid: { display: false },
                ticks: { precision: 0 },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    title: (context: any) => {
                        const date = moment(context[0].label);
                        return `${date.format("YYYY.MM.DD (dd)")}`;
                    },
                },
            },
        },
    };

    const scrollRef = useRef<HTMLDivElement | null>(null);

    return (
        <div css={subChartDiv}>
            <p css={titleP}>온비드 게스트 유입</p>
            <div className="mainChartBox">
                <div css={headerWrap}>
                    <DateRangeCalendar
                        setDate={initTime.day as DateRangeCalendarType}
                        getUserDate={(range: DateRangeCalendarType) =>
                            setFilter({
                                startDate: dateFormat(range.startDate),
                                endDate: dateFormat(range.endDate),
                            })
                        }
                    />
                </div>
                <div css={subContainer}>
                    <div css={subTitle}>
                        <LabelName nameColorBg={GUEST_COLOR} />
                        <p>접속자 수</p>
                        <span className="sum">
                            {summary.guest.toLocaleString()}
                        </span>
                    </div>
                    <div css={subTitle}>
                        <LabelName nameColorBg={TOKEN_COLOR} />
                        <p>매물 조회 수</p>
                        <span className="sum">
                            {summary.token.toLocaleString()}
                        </span>
                    </div>
                </div>
                <div style={{ height: "20px" }}></div>
                {isError ? (
                    <div css={messageStyle}>데이터 조회에 실패하였습니다.</div>
                ) : (
                    <MixedChart
                        data={chartData}
                        options={options}
                        onScrollEnd={(val: number) =>
                            scrollRef.current?.scrollTo(val, 0)
                        }
                        boxStyles={chartBoxStyle}
                    />
                )}
            </div>
        </div>
    );
}

const subChartDiv = css`
    width: 610px;
    height: 360px;
    padding: 16px;
    margin-bottom: 20px;
    border-radius: 8px;
    border: 1.5px solid ${Common.colors.gray200};
`;

const titleP = css`
    ${Common.textStyle.title7_M12}
    color: ${Common.colors.black};
    margin-bottom: 12px;
    font-size: 15px;
`;

const headerWrap = css`
    width: 100%;
    margin: 5px 0;
    display: flex;
    flex-direction: column;
`;

const chartBoxStyle = css`
    width: 550px;
    height: 220px;
    overflow: auto;
    ::-webkit-scrollbar {
        border-radius: 4px;
        width: 2px;
        height: 8px;
        margin-right: 10px;
    }
    ::-webkit-scrollbar-thumb {
        border-radius: 20px;
        background-color: #c9c9c9;
    }
    ::-webkit-scrollbar-track {
        border-radius: 20px;
        background-color: transparent;
    }
`;

const subTitle = css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    ${Common.textStyle.title7_M12}
    margin-right: 16px;
    .sum {
        margin-left: 6px;
        font-weight: 700;
        color: ${Common.colors.black};
    }
`;

const subContainer = css`
    display: flex;
    flex-direction: row;
`;

const messageStyle = css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 220px;
    ${Common.textStyle.title7_M12}
    color: ${Common.colors.gray700};
`;

const LabelName = ({ nameColorBg }: { nameColorBg: string }) => {
    const chartColor = css`
        width: 15px !important;
        height: 15px;
        margin: 8px 5px;
        border-radius: 3px;
        background-color: ${nameColorBg};
    `;
    return <div css={chartColor} />;
};

export default OnbidGuestStatsChart;
